import type { CommissionRule, LaneCard, ProductTypeCondition, AccommodationCondition, ProductTypeId} from '../CommissionManager/types';
import { syncMirroredLaneCards, type MirrorSyncContext } from '../CommissionManager/mirrorSync';
import type { Fee } from 'app/shared/model';
import type { CountryWithNumericId } from 'app/shared/api/getCountries';
import { getCountryApiIdFromRow } from './countryApiId';
import {
  CENTRE_RAW_ID_KEYS,
  extractCentreArrayPayload,
  parsePositiveCentreId,
  positiveNumericCentreId,
  type CentreListItem,
} from 'app/shared/api/getCentres';
import type { TaxBandRule } from './taxTypes';
import type { TaxRuleApiDto } from 'app/shared/api/taxRules/types';

export function generateCardId(): string {
  return `card-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function getSummary(condition: ProductTypeCondition | AccommodationCondition | undefined): string {
  if (!condition) return 'Specify product';
  if (condition.mode === 'ANY') return 'Any';
  if ('selectedNames' in condition && condition.selectedNames?.length) {
    return condition.selectedNames.join(', ');
  }
  if ('typeName' in condition && condition.typeName) return condition.typeName;
  return 'Specify product';
}

export function mapIdToName(rows: { id?: number | string; name?: string }[]): Map<number, string> {
  const m = new Map<number, string>();
  for (const row of rows) {
    const id = Number(row.id);
    if (Number.isFinite(id) && id > 0) m.set(id, String(row.name ?? ''));
  }
  return m;
}

export function normIdNameList(rows: { id?: number; name?: string }[]): { id: number; name: string }[] {
  return rows.map((x) => ({ id: x.id ?? 0, name: x.name ?? '' })).filter((x) => x.id > 0);
}

export function typeIdByProductMap(rows: { id?: number; typeId?: number }[]): Map<number, number> {
  return new Map(
    rows
      .filter((a) => (a.id ?? 0) > 0 && a.typeId != null && Number(a.typeId) > 0)
      .map((a) => [a.id as number, Number(a.typeId)])
  );
}

export function normTypeList(rows: { id?: number; name?: string }[]): { id: number; name: string }[] {
  return rows
    .map((t) => ({ id: Number(t.id) || 0, name: String(t.name ?? '') }))
    .filter((t) => t.id > 0);
}

export function buildMirrorSyncContext(
  courses: { id: number; name: string }[],
  accommodations: { id: number; name: string }[],
  addons: { id: number; name: string }[],
  transfers: { id: number; name: string }[],
  feesList: Fee[],
  accommodationTypeByProductId: Map<number, number>,
  addOnTypeByProductId: Map<number, number>
): MirrorSyncContext {
  return {
    getOptions: (pt) => {
      switch (pt) {
        case 'Course':
          return courses;
        case 'Accommodation':
          return accommodations;
        case 'AddOn':
          return addons;
        case 'Transfer':
          return transfers;
        case 'Fees':
          return feesList.map((f) => ({ id: f.id, name: f.name }));
        default:
          return [];
      }
    },
    accommodationTypeByProductId,
    addOnTypeByProductId,
  };
}

export function normalizeTaxCodeDigits(taxCode: string): string {
  return taxCode.replace(/\D/g, '');
}

export function taxCodeDigitsOkForUi(taxCode: string): boolean {
  const d = normalizeTaxCodeDigits(taxCode);
  return d.length >= 4 && d.length <= 7;
}

export type TaxCodeGlobalDupState = 'idle' | 'pending' | 'ok' | 'dup' | 'error';

export function parseApiBandIdFromRuleId(ruleId: string): number | undefined {
  const n = Number(ruleId);
  if (Number.isFinite(n) && n > 0) return Math.trunc(n);
  return undefined;
}

export function computeTaxCodeFieldError(opts: {
  raw: string;
  blurred: boolean;
  bandId: string;
  allBands: TaxBandRule[];
}): string | undefined {
  const digits = normalizeTaxCodeDigits(opts.raw);
  if (!opts.blurred && digits.length === 0) return undefined;
  if (opts.blurred && digits.length === 0) return 'Tax Code required';
  if (digits.length < 4 || digits.length > 7) {
    return 'Tax Code must be 4-7 digits';
  }
  const dupSameRule = opts.allBands.some(
    (b) =>
      b.id !== opts.bandId && normalizeTaxCodeDigits(b.taxCode) === digits
  );
  if (dupSameRule) return 'Already exists';
  return undefined;
}

export function mergeTaxCodeInlineError(
  local: string | undefined,
  globalDup: TaxCodeGlobalDupState | undefined
): string | undefined {
  if (local) return local;
  if (globalDup === 'dup') return 'Already exists';
  if (globalDup === 'error') return 'Could not verify tax code';
  return undefined;
}

export function isTaxCodeValidForSave(
  band: TaxBandRule,
  allBands: TaxBandRule[],
  globalDup: Record<string, TaxCodeGlobalDupState>
): boolean {
  const localErr = computeTaxCodeFieldError({
    raw: band.taxCode,
    blurred: true,
    bandId: band.id,
    allBands,
  });
  if (localErr) return false;
  const digits = normalizeTaxCodeDigits(band.taxCode);
  if (digits.length < 4 || digits.length > 7) return false;
  return globalDup[band.id] === 'ok';
}

export function taxCodeDigitsOkForApi(taxCode: string): boolean {
  const d = taxCode.replace(/\D/g, '');
  return d.length >= 4 && d.length <= 7;
}

export function countryOptionValue(c: CountryWithNumericId): number | string {
  if (c.numericId != null && c.numericId > 0) return c.numericId;
  const n = Number(c.id);
  if (Number.isFinite(n) && n > 0) return n;
  return String(c.id);
}

export function centreOptionValue(c: CentreListItem): number | string {
  const raw = c.id;
  const n = typeof raw === 'number' ? raw : Number(String(raw).trim());
  if (Number.isFinite(n) && n > 0) return n;
  return String(raw).trim();
}

export function extractCentreScopeLabelFromTaxRuleDto(dto: TaxRuleApiDto): string | undefined {
  const r = dto as Record<string, unknown>;
  const cn = r.centreNames ?? r.CentreNames;
  if (Array.isArray(cn)) {
    const labels = cn.map((x) => String(x)).filter(Boolean);
    if (labels.length) return labels.join(', ');
  }
  const centresArr = r.centres ?? r.Centres;
  if (Array.isArray(centresArr) && centresArr.length) {
    const first = centresArr[0] as Record<string, unknown>;
    const nm = first?.name ?? first?.Name;
    if (nm != null && String(nm).trim()) return String(nm).trim();
  }
  const single = r.centreName ?? r.CentreName;
  if (single != null && String(single).trim()) return String(single).trim();
  const centreObj = r.centre ?? r.Centre;
  if (centreObj && typeof centreObj === 'object') {
    const o = centreObj as Record<string, unknown>;
    const nm = o.name ?? o.Name;
    if (nm != null && String(nm).trim()) return String(nm).trim();
  }
  return undefined;
}

export function appendCentreSelectFallbackIfMissing(
  options: { value: number | string; label: string }[],
  centreId: number | string | null,
  specificCentre: boolean,
  fallbackLabel?: string | null
): { value: number | string; label: string }[] {
  if (!specificCentre || centreId == null || centreId === '') return options;
  const hasValue = options.some((o) => optionValueMatches(o.value, centreId));
  if (hasValue) return options;
  const idNum = extractNumericCentreId(centreId);
  const label =
    (fallbackLabel && fallbackLabel.trim()) ||
    (idNum > 0 ? `Centre (ID ${idNum})` : String(centreId));
  return [{ value: centreId, label }, ...options];
}

function optionValueMatches(optionVal: number | string, selected: number | string): boolean {
  if (optionVal === selected) return true;
  if (String(optionVal) === String(selected)) return true;
  const no = Number(optionVal);
  const ns = Number(selected);
  if (Number.isFinite(no) && Number.isFinite(ns) && no === ns) return true;
  return false;
}

export function countryIdForApi(value: number | string | null, countries: CountryWithNumericId[]): number {
  if (value == null || value === '') return 0;
  if (typeof value === 'number' && value > 0) return value;
  const asNum = Number(value);
  if (Number.isFinite(asNum) && asNum > 0) return asNum;
  const match = countries.find((x) => optionValueMatches(countryOptionValue(x), value));
  if (!match) return 0;
  const fromRow = getCountryApiIdFromRow(match);
  return fromRow > 0 ? fromRow : 0;
}

export function resolveTaxRuleCountryForPayload(
  value: number | string | null,
  countries: CountryWithNumericId[]
): number | string {
  const n = countryIdForApi(value, countries);
  if (n > 0) return n;
  const match =
    value != null && value !== ''
      ? countries.find((x) => optionValueMatches(countryOptionValue(x), value))
      : undefined;
  if (match?.id != null) {
    const code = String(match.id).trim();
    if (code !== '') return code;
  }
  return 0;
}

export function centreFilterCountryKeys(
  value: number | string | null,
  countries: CountryWithNumericId[]
): { numericCountryId: number; isoCode: string | null } {
  const numericCountryId =
    value != null && value !== '' ? countryIdForApi(value, countries) : 0;
  if (numericCountryId > 0) return { numericCountryId, isoCode: null };
  const match =
    value != null && value !== ''
      ? countries.find((x) => optionValueMatches(countryOptionValue(x), value))
      : undefined;
  const iso =
    match?.id != null && String(match.id).trim() !== '' ? String(match.id).trim() : null;
  return { numericCountryId: 0, isoCode: iso };
}

export function extractNumericCentreId(value: number | string | null | undefined): number {
  if (value == null || value === '') return 0;
  if (typeof value === 'number') return Number.isFinite(value) && value > 0 ? Math.trunc(value) : 0;
  const s = String(value).trim();
  if (!s) return 0;
  const direct = Number(s);
  if (Number.isFinite(direct) && direct > 0) return Math.trunc(direct);
  const digits = s.replace(/\D+/g, '');
  if (!digits) return 0;
  const parsed = parseInt(digits, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

export function numericCentreIdFromRaw(raw: unknown, depth = 0): number {
  if (depth > 4) return 0;
  if (raw == null || raw === '') return 0;
  if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) return Math.trunc(raw);
  if (typeof raw === 'bigint') {
    const x = Number(raw);
    return Number.isFinite(x) && x > 0 ? Math.trunc(x) : 0;
  }
  if (typeof raw === 'object' && raw !== null) {
    const o = raw as Record<string, unknown>;
    const nested = o.id ?? o.Id ?? o.value ?? o.Value;
    if (nested !== undefined && nested !== raw) return numericCentreIdFromRaw(nested, depth + 1);
  }
  const s = String(raw).trim();
  if (!s) return 0;
  const n = Number(s);
  if (Number.isFinite(n) && n > 0) return Math.trunc(n);
  return 0;
}

export function centreIdForApi(value: number | string | null, centres: CentreListItem[]): number {
  if (value == null || value === '') return 0;
  if (typeof value === 'number' && value > 0) return value;
  const asNum = Number(typeof value === 'string' ? value.trim() : value);
  if (Number.isFinite(asNum) && asNum > 0) return Math.trunc(asNum);
  const match = centres.find((x) => optionValueMatches(centreOptionValue(x), value));
  if (!match) return 0;
  const id = numericCentreIdFromRaw(match.id);
  return id > 0 ? id : 0;
}

export function resolveCentreIdForTaxSave(
  selected: number | string | null,
  allCentres: CentreListItem[],
  dropdownOptions: ReadonlyArray<{ value: number | string; label?: string }>
): number {
  const primary = centreIdForApi(selected, allCentres);
  if (primary > 0) return primary;
  if (selected == null || selected === '') return 0;

  const opt = dropdownOptions.find((o) => optionValueMatches(o.value, selected));
  if (!opt) return 0;

  const v = opt.value;
  const fromOpt = numericCentreIdFromRaw(v);
  if (fromOpt > 0) return fromOpt;
  return centreIdForApi(v, allCentres);
}

function normName(s: string): string {
  return s.trim().toLowerCase();
}

export function coerceCentreIdForTaxPayload(
  selected: number | string | null,
  allCentres: CentreListItem[],
  dropdownOptions: ReadonlyArray<{ value: number | string; label?: string }>
): number {
  let id = resolveCentreIdForTaxSave(selected, allCentres, dropdownOptions);
  if (id > 0) return id;
  if (selected == null || selected === '') return 0;
  id = numericCentreIdFromRaw(selected);
  if (id > 0) return id;
  for (const c of allCentres) {
    if (optionValueMatches(centreOptionValue(c), selected)) {
      id = numericCentreIdFromRaw(c.id);
      if (id > 0) return id;
    }
  }
  if (typeof selected === 'string' && selected.trim() !== '') {
    const key = normName(selected);
    const optByLabel = dropdownOptions.find(
      (o) => o.label != null && normName(String(o.label)) === key
    );
    if (optByLabel) {
      id = numericCentreIdFromRaw(optByLabel.value);
      if (id > 0) return id;
    }
    const rowByName = allCentres.find((c) => normName(String(c.name ?? '')) === key);
    if (rowByName) {
      id = numericCentreIdFromRaw(rowByName.id);
      if (id > 0) return id;
    }
  }
  return 0;
}

export function matchCountrySelectValue(
  apiCountryId: number | string,
  list: CountryWithNumericId[]
): number | string | null {
  if (apiCountryId === '' || apiCountryId == null) return null;
  if (typeof apiCountryId === 'string') {
    const s = apiCountryId.trim();
    if (!s) return null;
    const byCode = list.find((c) => String(c.id).toUpperCase() === s.toUpperCase());
    if (byCode) return countryOptionValue(byCode);
    const num = Number(s);
    if (Number.isFinite(num) && num > 0) {
      const byNumeric = list.find((c) => c.numericId === num);
      if (byNumeric) return countryOptionValue(byNumeric);
      const byNumId = list.find((c) => Number(c.id) === num);
      if (byNumId) return countryOptionValue(byNumId);
    }
    return s;
  }
  if (apiCountryId <= 0) return null;
  const byNumeric = list.find((c) => c.numericId === apiCountryId);
  if (byNumeric) return countryOptionValue(byNumeric);
  const byId = list.find((c) => Number(c.id) === apiCountryId);
  if (byId) return countryOptionValue(byId);
  return apiCountryId;
}

export function hasTaxCountryScopeSet(countryId: number | string): boolean {
  if (typeof countryId === 'number') return countryId > 0;
  return String(countryId).trim() !== '';
}

export function isCountrySelectionEmpty(value: number | string | null | undefined): boolean {
  if (value == null || value === '') return true;
  if (typeof value === 'number') return !Number.isFinite(value) || value <= 0;
  const s = String(value).trim();
  return s === '' || s === '0';
}

export function hasCentreSelectionForHeader(value: number | string | null | undefined): boolean {
  if (value == null || value === '') return false;
  if (typeof value === 'number') return Number.isFinite(value) && value > 0;
  const s = String(value).trim();
  return s !== '' && s !== '0';
}


export function buildCentreOptionValueByAnyId(
  centres: CentreListItem[],
  detailRaw: unknown | null
): Map<number, number | string> {
  const map = new Map<number, number | string>();
  const register = (id: number, opt: number | string) => {
    if (id > 0) map.set(id, opt);
  };
  for (const c of centres) {
    const opt = centreOptionValue(c);
    const pid = positiveNumericCentreId(c);
    if (pid != null) register(pid, opt);
    const idNum = Number(String(c.id).trim());
    if (Number.isFinite(idNum) && idNum > 0) register(Math.trunc(idNum), opt);
  }
  const rows =
    detailRaw == null
      ? []
      : Array.isArray(detailRaw)
        ? detailRaw
        : extractCentreArrayPayload(detailRaw);
  for (const row of rows) {
    if (!row || typeof row !== 'object') continue;
    const r = row as Record<string, unknown>;
    const rawIds: number[] = [];
    for (const key of CENTRE_RAW_ID_KEYS) {
      const n = parsePositiveCentreId(r[key]);
      if (n != null) rawIds.push(n);
    }
    if (!rawIds.length) continue;
    let matched: CentreListItem | undefined;
    for (const c of centres) {
      const listIds = new Set<number>();
      const pid = positiveNumericCentreId(c);
      if (pid != null) listIds.add(pid);
      const idNum = Number(String(c.id).trim());
      if (Number.isFinite(idNum) && idNum > 0) listIds.add(Math.trunc(idNum));
      if (rawIds.some((x) => listIds.has(x))) {
        matched = c;
        break;
      }
    }
    if (!matched) continue;
    const opt = centreOptionValue(matched);
    for (const id of rawIds) register(id, opt);
  }
  return map;
}

export function matchCentreSelectValue(
  apiCentreId: number,
  list: CentreListItem[],
  optionByTaxId?: Map<number, number | string>
): number | string | null {
  if (apiCentreId <= 0) return null;
  const mapped = optionByTaxId?.get(apiCentreId);
  if (mapped != null) return mapped;
  const c = list.find((x) => {
    const pid = positiveNumericCentreId(x);
    if (pid != null && pid === apiCentreId) return true;
    return Number(String(x.id).trim()) === apiCentreId;
  });
  if (c) return centreOptionValue(c);
  return apiCentreId;
}

export function taxCentreIdForSavePayload(
  selected: number | string | null,
  optionByTaxId: Map<number, number | string>
): number {
  if (selected == null || selected === '') return 0;
  const n = extractNumericCentreId(selected);
  if (n <= 0) return 0;
  for (const [taxId, optVal] of optionByTaxId.entries()) {
    if (optionValueMatches(optVal, selected)) return taxId;
  }
  return n;
}

export function emptyBand(): TaxBandRule {
  return {
    id: `band-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    name: '',
    include: [],
    exclude: [],
    active: true,
    taxRatePercent: 0,
    taxCode: '',
  };
}

export function newBandFromTaxName(taxNameTrimmed: string): TaxBandRule {
  const b = emptyBand();
  if (taxNameTrimmed) b.name = taxNameTrimmed;
  return b;
}

export function syncTaxBandMirrors(rule: TaxBandRule, ctx: MirrorSyncContext): TaxBandRule {
  return syncMirroredLaneCards(rule as unknown as CommissionRule, ctx) as unknown as TaxBandRule;
}

export function optionsForProductType(
  pt: ProductTypeId,
  lists: {
    courses: { id: number; name: string }[];
    accommodations: { id: number; name: string }[];
    addons: { id: number; name: string }[];
    transfers: { id: number; name: string }[];
    fees: Fee[];
  }
): { id: number; name: string }[] {
  switch (pt) {
    case 'Course':
      return lists.courses;
    case 'Accommodation':
      return lists.accommodations;
    case 'AddOn':
      return lists.addons;
    case 'Transfer':
      return lists.transfers;
    case 'Fees':
      return lists.fees.map((f) => ({ id: f.id, name: f.name }));
    default:
      return [];
  }
}

export function hasIncompleteProductSelection(rule: TaxBandRule): boolean {
  const checkCard = (card: LaneCard) => {
    if (card.isMirror) return false;
    const c = card.condition;
    if (!c) return false;
    if (c.mode === 'SELECT' && (!('selectedIds' in c) || !c.selectedIds?.length)) return true;
    if (c.mode === 'CHOOSE_TYPE' && (!('typeId' in c) || c.typeId == null || Number(c.typeId) <= 0)) return true;
    return false;
  };
  return rule.include.some(checkCard) || rule.exclude.some(checkCard);
}

export function formatTaxSaveErrorMessage(message?: string | null): string | undefined {
  if (message == null) return undefined;
  const trimmed = message.trim();
  if (!trimmed) return undefined;
  return trimmed.replace(/\b[Bb]and name\b/g, 'Rule name');
}